import { CollectionReference, Firestore, Unsubscribe, doc, onSnapshot } from 'firebase/firestore';
import { GetUserInteractor } from '../../../auth/domain/interactors/get-user.interactor';
import { GetTeamByIdInteractor } from '../../../team/domain/interactors/get-team-by-id.interactor';
import invariant from 'tiny-invariant';
import {
  AnyTeamEntityVersion,
  Collections,
  FirestoreEntity,
  Mapper,
  Team,
} from '@uploadcam/common';
import { SyncTeamSubscriptionInteractor } from './sync-team-subscription.interactor';
import { getCollectionRef } from '../../../../@harmony/angular';
import { Subject } from 'rxjs';

export class WaitPurchaseWebhookInteractor {
  private readonly collection: CollectionReference<AnyTeamEntityVersion>;

  constructor(
    private readonly db: Firestore,
    private readonly getTeamById: GetTeamByIdInteractor,
    private readonly getUser: GetUserInteractor,
    private readonly mapper: Mapper<FirestoreEntity<AnyTeamEntityVersion>, Team>,
    private readonly purchaseDone$: Subject<void>,
    private readonly syncTeamSubscription: SyncTeamSubscriptionInteractor,
  ) {
    this.collection = getCollectionRef(this.db, Collections.Teams);
  }

  public async execute(priceId: string): Promise<void> {
    const user = await this.getUser.execute();
    invariant(user.teamId, 'The user must be part of a team');
    const team = await this.getTeamById.execute(user.teamId);

    // Subscription was already updated, early return
    if (team.subscription?.priceId === priceId) {
      this.purchaseDone$.next();
      return;
    }

    // Watch Firestore document changes
    let timeoutId: ReturnType<typeof setTimeout>;
    let unsubscribe: Unsubscribe;

    return new Promise<void>((resolve, reject) => {
      // Add timeout logic in case the webhook takes too long
      timeoutId = setTimeout(async () => {
        try {
          // Try to force a team sync with Stripe
          await this.syncTeamSubscription.execute();
          resolve();
        } catch {
          reject(new Error('Timeout while waiting Stripe webhook.'));
        }

        unsubscribe();
      }, 60_000);

      // Watch Firestore
      unsubscribe = onSnapshot(doc(this.collection, team.id), (teamDoc) => {
        const updatedTeam = this.mapper.map(FirestoreEntity.from(teamDoc));

        // Updated!
        if (updatedTeam.subscription?.priceId === priceId) {
          unsubscribe();
          clearTimeout(timeoutId);
          resolve();
        }
      });
    }).then(() => {
      // Notify that the purchase is done
      this.purchaseDone$.next();
    });
  }
}
